import { useScreenWidth } from "@msys/ui";
import {
  Calculate as CalculateIcon,
  CreditCard as CreditCardIcon,
  EuroSymbol as EuroSymbolIcon,
  IntegrationInstructions as IntegrationInstructionsIcon,
  Palette as PaletteIcon,
  People as PeopleIcon,
  SupervisedUserCircle as SupervisedUserCircleIcon,
  SwitchAccount as SwitchAccountIcon,
  Web as WebIcon,
} from "@mui/icons-material";
import HandymanIcon from "@mui/icons-material/Handyman";
import WizardIcon from "@mui/icons-material/AutoFixHigh";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { RestrictedByCapability } from "../../auth/RestrictedByCapability.js";
import { RestrictedByOrganisationPermission } from "../../auth/RestrictedByOrganisationPermission.js";
import { useUserData } from "../../auth/useUserData.js";
import { PageNotFound } from "../../commons/PageNotFound.js";
import { PageLeftSidebarGroup } from "../../commons/layout/PageLeftSidebar.js";
import { PageTopbarItem } from "../../commons/layout/PageSubmenu.js";
import { ChatIcon } from "../../features/channels/ChatIcon.js";
import { DocumentIcon } from "../../features/documents/DocumentIcon.js";
import { useM1Flows } from "../../features/m1-calculators/useM1Integration.js";
import { OrganisationIcon } from "../../features/organisations/OrganisationIcon.js";
import { PlanningIcon } from "../../features/planning/PlanningIcon.js";
import {
  useSectionSidebarConfig,
  useSidebarGroups,
} from "../../global/SidebarProvider.js";
import { OrganisationBranding } from "./OrganisationBranding.js";
import { OrganisationCalculator } from "./OrganisationCalculator.js";
import { OrganisationChats } from "./OrganisationChats.js";
import { OrganisationDetails } from "./OrganisationDetails.js";
import { OrganisationMembers } from "./OrganisationMembers.js";
import { OrganisationPayment } from "./OrganisationPayment.js";
import { OrganisationSettingsDocuments } from "./OrganisationSettingsDocuments.js";
import { OrganisationSettingsIntegrations } from "./OrganisationSettingsIntegrations.js";
import { OrganisationSettingsOfferedServices } from "./OrganisationSettingsOfferedServices.js";
import { OrganisationSettingsPlanning } from "./OrganisationSettingsPlanning.js";
import { OrganisationSettingsPricesAndCalculations } from "./OrganisationSettingsPricesAndCalculations.js";
import { MyOrganisationUserProfile } from "./OrganisationUserProfile.js";
import { OrganisationWebsites } from "./OrganisationWebsites.js";
import { OrganisationWizardStyling } from "./OrganisationWizardStyling.js";

const EMPTY_ARRAY: any[] = [];

type PageSubmenuItem = PageTopbarItem & { isHidden?: boolean };

export const OrganisationRoutes = () => {
  const viewer = useUserData().currentUser!;

  const { t } = useTranslate([
    "OrganisationPageTopbar",
    "OrganisationBranding",
  ]);
  const { isMinLargeDesktop } = useScreenWidth();

  const { meister1Flows } = useM1Flows(viewer.organisation.id);

  const meister1FlowsIds = meister1Flows
    .map(f => [f.id, f.identName, f.label].join("-"))
    .join("/");

  const sidebarGroups = React.useMemo(() => {
    if (!viewer.organisation) return [];

    const groups: PageLeftSidebarGroup<PageSubmenuItem>[] = [];
    const organisationItems: PageSubmenuItem[] = [
      {
        name: "organisation-profile",
        label: t("Organisation Details", { ns: "OrganisationPageTopbar" }),
        path: `/organisation/profile`,
        icon: <OrganisationIcon />,
      },
      {
        name: "organisation-members",
        label: t("Members", { ns: "OrganisationPageTopbar" }),
        path: `/organisation/members`,
        icon: <PeopleIcon />,
        permission: "MANAGE_ORG",
      },
      {
        name: "organisation-payment-accounting",
        label: t("Payment & Accounting", { ns: "OrganisationPageTopbar" }),
        path: `/organisation/payment-accounting`,
        icon: <CreditCardIcon />,
      },
      {
        name: "organisation-branding",
        label: t("Branding", { ns: "OrganisationPageTopbar" }),
        path: `/organisation/branding`,
        icon: <PaletteIcon />,
        capability: "BRANDING",
        permission: "MANAGE_ORG",
        isHidden: !viewer.organisation.isCraftsmanOrganisation,
      },
      {
        name: "organisation-chats",
        label: t("Internal chat", { ns: "OrganisationPageTopbar" }),
        path: `/organisation/chats`,
        icon: <ChatIcon />,
      },
    ];
    groups.push({
      name: t("Organisation", { ns: "OrganisationPageTopbar" }),
      items: organisationItems,
    });

    const advancedSettingsItems: PageSubmenuItem[] = [
      {
        // TODO: migrate the CRAFTSMAN-only gate to a capability check once an
        // `OFFERED_SERVICES` (or similar) value is added to the Capabilities enum.
        name: "organisation-settings-offered-services",
        label: t("Offered services", {
          ns: "OrganisationPageTopbar",
        }),
        path: `/organisation/settings/offered-services`,
        icon: <HandymanIcon />,
        permission: "MANAGE_ORG",
        isHidden: !viewer.organisation.isCraftsmanOrganisation,
      },
      {
        name: "organisation-settings-prices-and-calculations",
        label: t("Prices and Calculations", {
          ns: "OrganisationPageTopbar",
        }),
        path: `/organisation/settings/prices-and-calculations`,
        icon: <EuroSymbolIcon />,
        permission: "MANAGE_ORG",
        isHidden: !viewer.organisation.isCraftsmanOrganisation,
      },
      {
        name: "organisation-settings-documents",
        label: t("Documents", { ns: "OrganisationPageTopbar" }),
        path: `/organisation/settings/documents`,
        icon: <DocumentIcon />,
        permission: "MANAGE_ORG",
        isHidden: !viewer.organisation.isCraftsmanOrganisation,
      },
      {
        name: "organisation-settings-integrations",
        label: t("Data & Integrations", {
          ns: "OrganisationPageTopbar",
        }),
        path: `/organisation/settings/integrations`,
        icon: <IntegrationInstructionsIcon />,
        permission: "MANAGE_ORG",
        isHidden: !viewer.organisation.isCraftsmanOrganisation,
      },
      {
        name: "organisation-settings-planning",
        label: t("Planning & Execution", {
          ns: "OrganisationPageTopbar",
        }),
        path: `/organisation/settings/planning`,
        icon: <PlanningIcon />,
        permission: "MANAGE_ORG",
        isHidden: !viewer.organisation.isCraftsmanOrganisation,
      },
    ];
    groups.push({
      name: t("Settings", { ns: "OrganisationPageTopbar" }),
      items: advancedSettingsItems,
      capability: "ADVANCED_SETTINGS",
      permission: "MANAGE_ORG",
    });

    const integrationsItems: PageSubmenuItem[] = [
      {
        name: "organisation-integrations-websites",
        label: t("Websites", {
          ns: "OrganisationPageTopbar",
        }),
        path: `/organisation/integrations/websites`,
        icon: <WebIcon />,
        permission: "MANAGE_ORG",
        capability: "WEBSHOPCMS",
        isHidden: !viewer.organisation.isCraftsmanOrganisation,
      },
    ];
    groups.push({
      name: t("Integrations", { ns: "OrganisationPageTopbar" }),
      items: integrationsItems,
      permission: "MANAGE_ORG",
      anyCapabilities: ["WEBSHOPCMS", "CALL_ASSISTANT"],
    });

    groups.push({
      name: t("Calculators", { ns: "OrganisationPageTopbar" }),
      items: meister1Flows.map(flow => ({
        name: "organisation-settings-calculator",
        label: flow.label,
        path: `/organisation/calculators/${flow.id}`,
        icon: <CalculateIcon />,
        permission: "MANAGE_ORG",
        isHidden: !viewer.organisation.isCraftsmanOrganisation,
      })),
      capability: "ADVANCED_SETTINGS",
      permission: "MANAGE_ORG",
    });

    return groups.map(group => ({
      ...group,
      items: group.items.filter(item => !item.isHidden),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, viewer.organisation, meister1FlowsIds]);

  useSidebarGroups(sidebarGroups);
  useSectionSidebarConfig(
    React.useMemo(
      () => ({
        name: "organisation",
        isCollapsible: true,
        variant: "dense",
        leftColumnWidth: isMinLargeDesktop ? "sm" : "xs",
      }),
      [isMinLargeDesktop]
    )
  );

  const brandingSubmenuItems = React.useMemo(() => {
    if (!viewer.organisation) return [];

    const items: PageSubmenuItem[] = [
      {
        name: "organisation-branding-own",
        label: t("Own", {
          ns: "OrganisationBranding",
        }),
        path: `/organisation/branding/own`,
        icon: <SupervisedUserCircleIcon />,
        permission: "MANAGE_ORG",
        isHidden: !viewer.organisation.isCraftsmanOrganisation,
      },
      {
        name: "organisation-branding-referral",
        label: t("Referral", {
          ns: "OrganisationBranding",
        }),
        path: `/organisation/branding/referral`,
        icon: <SwitchAccountIcon />,
        capability: "REFERRAL",
        permission: "MANAGE_ORG",
        isHidden: !viewer.organisation.isCraftsmanOrganisation,
      },
      // {
      //   name: "organisation-branding-sponsoring",
      //   label: t("Sponsoring", { ns: "OrganisationBranding" }),
      //   type: "link",
      //   path: `/organisation/branding/sponsoring`,
      //   organisation,
      // },
      {
        name: "organisation-branding-wizard",
        label: t("Wizard", {
          ns: "OrganisationBranding",
        }),
        path: `/organisation/branding/wizard`,
        icon: <WizardIcon />,
        permission: "MANAGE_ORG",
        isHidden: !viewer.organisation.isCraftsmanOrganisation,
      },
    ];

    return items.filter(item => !item?.isHidden);
  }, [t, viewer.organisation]);

  return (
    <Routes>
      <Route index element={<Navigate to="profile" replace />} />

      <Route
        path="profile"
        element={<OrganisationDetails submenuItems={EMPTY_ARRAY} />}
      />
      <Route
        path="members"
        element={
          <RestrictedByOrganisationPermission permission="MANAGE_ORG">
            <OrganisationMembers submenuItems={EMPTY_ARRAY} />
          </RestrictedByOrganisationPermission>
        }
      />
      <Route
        path="payment-accounting"
        element={<OrganisationPayment submenuItems={EMPTY_ARRAY} />}
      />
      <Route
        path="branding"
        element={
          <RestrictedByCapability capability="BRANDING">
            <RestrictedByOrganisationPermission permission="MANAGE_ORG">
              <Outlet />
            </RestrictedByOrganisationPermission>
          </RestrictedByCapability>
        }
      >
        <Route index element={<Navigate to="own" replace />} />
        <Route
          path="own"
          element={
            <OrganisationBranding
              submenuItems={brandingSubmenuItems}
              section="own"
            />
          }
        />
        <Route
          path="referral"
          element={
            <RestrictedByCapability capability="REFERRAL">
              <OrganisationBranding
                submenuItems={brandingSubmenuItems}
                section="referral"
              />
            </RestrictedByCapability>
          }
        />
        <Route
          path="wizard"
          element={
            <OrganisationWizardStyling submenuItems={brandingSubmenuItems} />
          }
        />
      </Route>
      <Route
        path="chats"
        element={
          <OrganisationChats
            organisationId={viewer.organisation.id}
            submenuItems={EMPTY_ARRAY}
          />
        }
      />
      <Route
        path={"integrations/*"}
        element={
          <RestrictedByOrganisationPermission permission="MANAGE_ORG">
            <Outlet />
          </RestrictedByOrganisationPermission>
        }
      >
        <Route index element={<Navigate to="websites" replace />} />
        <Route
          path="websites"
          element={
            <RestrictedByCapability capability="WEBSHOPCMS">
              <OrganisationWebsites submenuItems={EMPTY_ARRAY} />
            </RestrictedByCapability>
          }
        />
      </Route>

      <Route
        path={"settings/*"}
        element={
          <RestrictedByCapability capability="ADVANCED_SETTINGS">
            <RestrictedByOrganisationPermission permission="MANAGE_ORG">
              <Outlet />
            </RestrictedByOrganisationPermission>
          </RestrictedByCapability>
        }
      >
        <Route index element={<Navigate to="documents" replace />} />
        {/* TODO: swap the CRAFTSMAN gate for a capability check (e.g.
            `RestrictedByCapability capability="OFFERED_SERVICES"`) once that
            value exists in the Capabilities enum. */}
        {viewer.organisation.organisationType === "CRAFTSMAN" && (
          <Route
            path="offered-services"
            element={
              <OrganisationSettingsOfferedServices submenuItems={EMPTY_ARRAY} />
            }
          />
        )}
        <Route
          path="prices-and-calculations"
          element={
            <OrganisationSettingsPricesAndCalculations
              submenuItems={EMPTY_ARRAY}
            />
          }
        />
        <Route
          path="documents"
          element={<OrganisationSettingsDocuments submenuItems={EMPTY_ARRAY} />}
        />
        <Route
          path="integrations"
          element={
            <OrganisationSettingsIntegrations submenuItems={EMPTY_ARRAY} />
          }
        />
        <Route
          path="planning"
          element={<OrganisationSettingsPlanning submenuItems={EMPTY_ARRAY} />}
        />
      </Route>
      <Route
        path="calculators/:calculatorId"
        element={
          <RestrictedByCapability capability="ADVANCED_SETTINGS">
            <RestrictedByOrganisationPermission permission="MANAGE_ORG">
              <OrganisationCalculator
                organisationId={viewer.organisation.id}
                submenuItems={EMPTY_ARRAY}
              />
            </RestrictedByOrganisationPermission>
          </RestrictedByCapability>
        }
      />
      <Route path={"users/:userId/*"}>
        <Route index element={<Navigate to="profile" replace />} />
        <Route
          path="profile"
          element={<MyOrganisationUserProfile submenuItems={EMPTY_ARRAY} />}
        />
      </Route>

      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};
